<template>
  <InfoPageLayout>
    <template #title>{{ t('title') }}</template>
    <div class="mt-16 border-b border-tw-ink/40">
      <details v-for="item in items" :key="item.key" class="group border-t border-tw-ink/40 first:border-t-0">
        <summary
          class="flex cursor-pointer list-none items-center justify-between gap-6 py-6 font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-tw-rust [&::-webkit-details-marker]:hidden"
        >
          {{ t(`${item.key}.q`) }}
          <svg
            class="h-6 w-6 shrink-0 text-tw-rust transition-transform group-open:rotate-45 motion-reduce:transition-none"
            viewBox="0 0 25 25"
            aria-hidden="true"
          >
            <path d="M0 10h25v5H0zM10 0h5v25h-5z" fill="currentColor" />
          </svg>
        </summary>
        <div class="space-y-8 pb-6 pr-10">
          <p v-for="n in item.paragraphs" :key="n">{{ t(`${item.key}.a${n}`) }}</p>
        </div>
      </details>
    </div>
  </InfoPageLayout>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'

  import InfoPageLayout from '@/components/landingpage/InfoPageLayout.vue'

  const items = [
    { key: 'pin', paragraphs: 2 },
    { key: 'export', paragraphs: 1 },
    { key: 'boards', paragraphs: 3 },
    { key: 'join', paragraphs: 3 },
    { key: 'contribution', paragraphs: 1 },
    { key: 'image', paragraphs: 1 },
    { key: 'copy', paragraphs: 1 },
    { key: 'participants', paragraphs: 1 },
    { key: 'pan', paragraphs: 1 },
    { key: 'missingWall', paragraphs: 1 },
    { key: 'exportWall', paragraphs: 3 },
  ]

  const { t } = useI18n({
    messages: {
      no: {
        title: 'Ofte stilte spørsmål',
        pin: {
          q: 'Hvorfor fungerer ikke PIN-koden min lenger?',
          a1: 'Etter en ny versjon av Talkwall 12. august 2025, ble det besluttet å deaktivere PIN-koder for eksisterende vegger av hensyn til personvern, for å hindre tilgang til vegger som ikke har vært brukt på en stund.',
          a2: 'Vi forstår at noen av disse veggene fortsatt kan være i bruk. Dersom du trenger tilgang med PIN-kode, ber vi deg kontakte den som laget veggen for å aktivere en ny PIN i veggens innstillingspanel.',
        },
        export: {
          q: 'Hvorfor ser jeg ikke lenger valget «eksporter» i vegg-menyen?',
          a1: 'Etter den nye versjonen som ble lansert 12. august 2025, har vi også valgt å begrense eksportfunksjonen til innloggede personer; de som laget veggen og personer du har delt tilgang med gjennom Feide-grupper.',
        },
        boards: {
          q: 'Hva betyr den nye knappen som bytter mellom «Én vegg» og «Mange vegger»?',
          a1: 'For hver oppgave er det mulig å vise eller skjule ekstra tavler (som vises vertikalt under den delte veggen representert med det grønne, runde ikonet øverst) på følgende måte:',
          a2: '«Én tavle»: Deltakerne bruker kun den delte tavlen og får ikke sin egen tavle når de blir med på veggen.',
          a3: '«Flere tavler»: Deltakerne kan bruke både den delte tavlen og sin egen tavle når de blir med. I tillegg kan arrangøren lage flere tavler ved å trykke på «+»-knappen under den vertikale listen med tavler.',
        },
        join: {
          q: 'Hvordan blir man med på en tavle?',
          a1: 'Organisator lager en pin kode ved å trykke på «dele» knappen. Denne pin koden kan brukes til å bli med som deltaker.',
          a2: 'Alternativt kan organisatoren dele en lenke med deg som du kan benytte for å bli med på tavla uten pin kode.',
          a3: 'Bemerk at om deltakere blir med mer enn 30 minutter etter at organisator har startet opp tavla, så vil organisator kanskje måtte trykke «oppdater» i nettleseren sin.',
        },
        contribution: {
          q: 'Hvordan legger man til et bidrag på en tavle?',
          a1: 'For å legge til et bidrag, klikker man på «+» knappen, og ikke slik som tidligere der man dobbeltklikket på tavlen for å laget et bidrag.',
        },
        image: {
          q: 'Hvordan legger man til et bilde i et bidrag?',
          a1: 'Man legger til et bilde før man legger til tekst',
        },
        copy: {
          q: 'Hvordan fungerer kopiering av tavler? Kommer alt innhold med?',
          a1: 'Når man kopierer tavler, kommer alt innhold med, også bidragene fra deltakerne på tavlene.',
        },
        participants: {
          q: 'Kan man fjerne deltakere?',
          a1: 'Det er dessverre ikke mulig å fjerne deltakere etter at de har blitt lagt til.',
        },
        pan: {
          q: 'Kan man flytte rundt på tavlen?',
          a1: 'Ja. Med mus klikker du på et tomt område og drar, med styreflate sveiper du med to fingre, og på nettbrett og mobil drar du med én finger. Du zoomer med scrollhjulet, ved å knipe med to fingre, eller med pluss- og minusknappene ved siden av tavla. Med tastatur går du til tavla med Tab: piltastene flytter visningen, pluss og minus zoomer, og 0 tilbakestiller zoomen.',
        },
        missingWall: {
          q: 'Jeg ser ikke veggen jeg laget på oversikten over vegger?',
          a1: 'Dette løses vanligvis ved å trykke på «oppdater» i nettleseren.',
        },
        exportWall: {
          q: 'Kan man eksportere tavler?',
          a1: 'Ja, man kan normalt eksportere tavler. Det kan dog hende man får en feilmelding på eksport hvis man har veldig mange innlegg og/eller innlegg med store bilder.',
          a2: 'Man eksporterer ved at organisator av tavlen trykker på de tre horisontale linjene og velger deretter menyvalget «eksporter».',
          a3: 'Deretter velger man knappen «eksporter» og deretter vil eksportfila lastes ned.',
        },
      },
      en: {
        title: 'Frequently asked questions',
        pin: {
          q: 'Why does my PIN code no longer work?',
          a1: 'Following a new version of Talkwall on 12 August 2025, PIN codes for existing walls were deactivated for privacy reasons, to prevent access to walls that have not been used for a while.',
          a2: 'We understand that some of these walls may still be in use. If you need access with a PIN code, please ask the person who created the wall to activate a new PIN in the wall’s settings panel.',
        },
        export: {
          q: 'Why can I no longer see the “export” option in the wall menu?',
          a1: 'Since the new version launched on 12 August 2025, export has been limited to logged-in users: the person who created the wall, and people it has been shared with through Feide groups.',
        },
        boards: {
          q: 'What does the new button that switches between “One wall” and “Many walls” do?',
          a1: 'For each task you can show or hide extra boards (shown vertically below the shared wall, which is represented by the round green icon at the top) as follows:',
          a2: '“One board”: Participants only use the shared board and do not get their own board when they join the wall.',
          a3: '“Several boards”: Participants can use both the shared board and their own board when they join. The organiser can also create more boards using the “+” button below the vertical list of boards.',
        },
        join: {
          q: 'How do I join a wall?',
          a1: 'The organiser creates a PIN code by pressing the “share” button. This PIN code can be used to join as a participant.',
          a2: 'Alternatively, the organiser can share a link with you that lets you join the wall without a PIN code.',
          a3: 'Note that if participants join more than 30 minutes after the organiser opened the wall, the organiser may need to press “refresh” in their browser.',
        },
        contribution: {
          q: 'How do I add a contribution to a wall?',
          a1: 'To add a contribution, click the “+” button. Double-clicking the board to create a contribution, as in earlier versions, no longer works.',
        },
        image: {
          q: 'How do I add an image to a contribution?',
          a1: 'Add the image before you add the text.',
        },
        copy: {
          q: 'How does copying walls work? Is all the content included?',
          a1: 'When you copy a wall, all of its content is included, including the participants’ contributions on its boards.',
        },
        participants: {
          q: 'Can participants be removed?',
          a1: 'Unfortunately, participants cannot be removed once they have been added.',
        },
        pan: {
          q: 'Can I move around the board?',
          a1: 'Yes. With a mouse, click an empty area and drag; with a trackpad, swipe with two fingers; on tablets and phones, drag with one finger. Zoom with the scroll wheel, by pinching with two fingers, or with the plus and minus buttons beside the board. With a keyboard, Tab to the board: the arrow keys move the view, plus and minus zoom, and 0 resets the zoom.',
        },
        missingWall: {
          q: 'Why can’t I see the wall I created in my list of walls?',
          a1: 'This is usually solved by pressing “refresh” in your browser.',
        },
        exportWall: {
          q: 'Can walls be exported?',
          a1: 'Yes, walls can normally be exported. You may get an error message when exporting a wall with a very large number of posts and/or posts with large images.',
          a2: 'To export, the organiser of the wall presses the three horizontal lines and then chooses “export” from the menu.',
          a3: 'Then press the “export” button and the export file will be downloaded.',
        },
      },
    },
  })
</script>
